import { useLang } from "../context/LangContext";
import { IMAGES } from "../images";
import { waLink } from "../constants";
import { Reveal } from "./Reveal";

const RateCard = ({ plan, index, content, lang }) => {
  const isCustom = plan.rate === "CUSTOM";
  const msg = isCustom
    ? lang === "cn"
      ? `你好，我想咨询 ${plan.spend} 大客户专属费率方案。`
      : `Hi, I'm interested in the ${plan.spend} dedicated client plan.`
    : lang === "cn"
      ? `你好，我想申请 ${plan.rate} 优惠费率方案（${plan.spend}）。`
      : `Hi, I'd like to apply for the ${plan.rate} rate plan (${plan.spend}).`;

  return (
    <Reveal
      delay={index * 0.07}
      className={`h-full ${plan.featured ? "lg:-translate-y-4" : ""}`}
    >
      <div
        data-testid={`pricing-tier-${index}`}
        className={`group relative flex h-full flex-col rounded-3xl p-6 transition-all duration-500 lg:p-7 ${
          plan.featured
            ? "glow-card bg-gradient-to-b from-blue-950/70 via-navy-900/95 to-navy-950 shadow-[0_0_50px_rgba(59,130,246,0.35)]"
            : "glass hover:border-white/20 hover:bg-white/[0.04]"
        }`}
      >
        {plan.featured && (
          <>
            <div className="animate-pulse-glow pointer-events-none absolute -top-14 left-1/2 h-32 w-40 -translate-x-1/2 rounded-full bg-blue-500/30 blur-[50px]" />
            <span className="absolute -top-3.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-to-r from-amber-300 to-amber-500 px-4 py-1.5 text-[11px] font-black tracking-wider text-slate-950 shadow-[0_0_24px_rgba(251,191,36,0.5)]">
              ★ {plan.badge}
            </span>
          </>
        )}

        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{content.monthlySpend}</p>
          <p className={`mt-2 min-h-[2.6rem] font-display text-sm font-bold leading-snug ${plan.featured ? "text-blue-200" : "text-slate-300"}`}>
            {plan.spend}
          </p>

          <div className="mt-6">
            {isCustom ? (
              <span className="text-gradient font-display text-[2rem] font-black leading-none tracking-tight">
                {plan.rateText}
              </span>
            ) : (
              <span
                className={`font-display text-[2.9rem] font-black leading-none tracking-tight ${
                  plan.featured ? "text-gradient" : "text-white"
                }`}
              >
                {plan.rate}
              </span>
            )}
          </div>
          <p className={`mt-2.5 text-sm font-semibold ${plan.featured ? "text-blue-300" : "text-slate-400"}`}>
            {plan.label}
          </p>
          <p className="mt-3 text-[13px] leading-relaxed text-slate-500">{plan.desc}</p>
        </div>

        <a
          href={waLink(msg)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`pricing-tier-cta-${index}`}
          className={`mt-7 flex items-center justify-center rounded-full py-3 text-sm font-bold transition-transform duration-300 hover:scale-[1.03] ${
            plan.featured
              ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-[0_0_28px_rgba(59,130,246,0.5)]"
              : "border border-white/12 bg-white/5 text-slate-200 hover:border-blue-400/40 hover:text-white"
          }`}
        >
          {plan.cta}
        </a>
      </div>
    </Reveal>
  );
};

export const Pricing = () => {
  const { t, lang } = useLang();
  const c = t.pricing;
  return (
    <section id="pricing" className="relative py-20 lg:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400">{c.tag}</span>
          <h2 className="mt-4 font-display text-3xl font-black text-white sm:text-4xl lg:text-5xl">{c.title}</h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{c.subtitle}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 lg:gap-4">
          {c.plans.map((plan, i) => (
            <RateCard key={i} plan={plan} index={i} content={c} lang={lang} />
          ))}
        </div>

        {/* 0.9% rate highlight visual */}
        <Reveal delay={0.1} className="mt-16">
          <div className="relative mx-auto max-w-5xl">
            <div className="absolute -inset-5 rounded-[32px] bg-gradient-to-r from-blue-600/20 via-violet-600/15 to-cyan-500/15 blur-2xl" />
            <div className="glow-card relative overflow-hidden rounded-2xl shadow-[0_20px_60px_rgba(2,6,23,0.7)] lg:rounded-3xl">
              <img
                src={IMAGES[lang].rate.src}
                width={IMAGES[lang].rate.w}
                height={IMAGES[lang].rate.h}
                alt="MB PAY VIA — 0.9% Exclusive Promotional Rate"
                className="block h-auto w-full"
                loading="lazy"
                data-testid="pricing-rate-visual"
              />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mx-auto mt-10 max-w-3xl text-center text-xs leading-relaxed text-slate-600">
            {c.disclaimer}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
